import type { FC, PropsWithChildren } from "react";
import React, { memo, useCallback, useId, useRef, useState } from "react";
import clsx from "clsx";
import styles from "./Selection.module.scss";
import { useCloseOnOutsideClick } from "../../../../../../../../../common-deprecated/hooks";
import Icon from "../../../../../../../shared-components/css-modules/lexus/atoms/Icon";
import Text from "../../../../../../../shared-components/css-modules/lexus/atoms/Text";
import type { AtmosphereType } from "../../../../../../utils/types/AtmosphereType";
import Price from "../../../../common/price/Price";
import { trackGradeCompare } from "../../../../../../utils/tracking";

const Select: FC<
    PropsWithChildren<{
        label: string;
        options: AtmosphereType[];
        selectedOption?: AtmosphereType;
        setSelectedOption: (option: AtmosphereType) => void;
    }>
> = ({ label, options, selectedOption, setSelectedOption }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setOpen] = useState<boolean>();

    const uniqueId = useId();

    const handleOptionClick = useCallback(
        (option: AtmosphereType) => () => {
            setOpen(false);
            trackGradeCompare(option.webtitleSections.grade);
            setSelectedOption(option);
        },
        [setSelectedOption],
    );

    const toggle = useCallback(() => {
        setOpen((newIsOpen) => !newIsOpen);
    }, []);
    const close = useCallback(() => {
        setOpen(false);
    }, []);

    useCloseOnOutsideClick(containerRef, close);

    return (
        <div className={styles.select} ref={containerRef}>
            <button
                aria-label={label}
                aria-haspopup="true"
                aria-controls={uniqueId}
                aria-expanded={isOpen}
                onClick={toggle}
                type="button"
                className={clsx(styles.button, isOpen && styles.open)}
            >
                {selectedOption ? (
                    <span>
                        <Text variant="apheleia-h5" tag="span" className={styles.grade}>
                            {selectedOption.webtitleSections.grade || selectedOption.webtitleSections.model}
                        </Text>
                        <span>
                            <Price price={selectedOption.price} />
                        </span>
                    </span>
                ) : (
                    <Text variant="apheleia-h5" tag="span" className={styles.placeholder}>
                        {label}
                    </Text>
                )}

                <Icon variant="chevron-down" sizing="xs" className={styles.icon} aria-hidden="true" />
            </button>
            {isOpen && (
                <div className={styles.listbox}>
                    <ul id={uniqueId} role="listbox" tabIndex={-1}>
                        {options.map((option) => {
                            const isSelected = selectedOption?.id === option.id;

                            return (
                                <li role="option" aria-selected={isSelected} aria-disabled={isSelected} key={option.id}>
                                    <button type="button" disabled={isSelected} onClick={handleOptionClick(option)}>
                                        <Text variant="apheleia-h5" tag="span" className={styles.grade}>
                                            {option.webtitleSections.grade || option.webtitleSections.model}
                                        </Text>
                                        <Price price={option.price} />
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

export default memo(Select);
